import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import {
	ActivityIndicator, Button, Modal, Searchbar, Text, useTheme,
} from '@/components/ui';
import { useDriveFolders, type DriveFolder } from '@/hooks/use-document-folders';

/**
 * Pick an association's document folder from what is actually on the drive.
 *
 * Exists because the failure this override fixes is a NAME nobody can be expected to type:
 * `WESTMINSTER VILLAGE CONDO` differs from the association's own name in case, in wording and
 * in length. Asking someone to retype it out of the Dropbox web UI would reintroduce, by
 * hand, exactly the mismatch the pin is there to repair.
 *
 * ## Unclaimed folders come first
 *
 * A folder needing a pin is one no association resolves to, so those are the reason anyone
 * opens this. Burying them among sixty correctly-linked communities would make the useful
 * half of the list the half you have to scroll past.
 *
 * Claimed folders are still SHOWN, though, and labelled with who claims them. Without them
 * there is no signal that a folder is already another community's, and two associations
 * pinned to one folder would merge two portfolios' documents in the browser with nothing
 * anywhere to explain why.
 *
 * ## Filtered in memory
 *
 * Per the repo's stable-search-input rule (CLAUDE.md): the whole list arrives in one query
 * and every keystroke filters the array. Tying the filter to a network key would blank the
 * list between characters, and the list costs four Dropbox listings to rebuild.
 */

/**
 * " · linked to A and B", " · linked to A", or " · not linked".
 *
 * Falls back to the scalar `associationName` so a response from a server predating
 * `associations` still reads correctly rather than claiming the folder is unlinked.
 */
function claimantLabel(folder: DriveFolder): string {
	const names = folder.associations?.map((a) => a.name)
		?? (folder.associationName ? [folder.associationName] : []);
	if (names.length === 0) return ' · not linked';
	if (names.length === 1) return ` · linked to ${names[0]}`;
	return ` · linked to ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export interface DropboxFolderPickerProps {
	visible: boolean;
	onDismiss: () => void;
	/** Called with `<Bucket>/<Folder Name>` exactly as the drive spells it. */
	onSelect: (path: string) => void;
	/** The currently pinned path, highlighted in the list. */
	current?: string | null;
}

export const DropboxFolderPicker: React.FC<DropboxFolderPickerProps> = ({
	visible,
	onDismiss,
	onSelect,
	current,
}) => {
	const theme = useTheme();
	const [query, setQuery] = useState('');
	// Only fetch once the picker is actually opened — four Dropbox listings is not a cost to
	// pay on every render of an association edit screen.
	const { data, isLoading, error } = useDriveFolders(visible);

	// Memoised, not a bare `data?.folders ?? []`. The `??` fallback allocates a NEW array on
	// every render while the query is settling, which would re-run the filter below every
	// time — and, once the list is 250 rows, on every keystroke twice over.
	const folders = useMemo(() => data?.folders ?? [], [data]);

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return folders;
		return folders.filter((f) => f.path.toLowerCase().includes(needle)
			|| (f.associationName ?? '').toLowerCase().includes(needle));
	}, [folders, query]);

	const unlinkedCount = folders.filter((f) => !f.associationId).length;

	const renderRow = (folder: DriveFolder) => {
		const isCurrent = current != null && folder.path.toLowerCase() === current.toLowerCase();
		return (
			<Pressable
				key={folder.path}
				onPress={() => { onSelect(folder.path); onDismiss(); }}
				style={({ pressed }) => ({
					paddingVertical: 10,
					paddingHorizontal: 12,
					borderRadius: 6,
					backgroundColor: isCurrent
						? theme.colors.primaryContainer
						: pressed ? theme.colors.surfaceVariant : 'transparent',
				})}
				testID={`dropbox-folder-option-${folder.path}`}
			>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
					<Ionicons
						name={folder.associationId ? 'link' : 'folder-open-outline'}
						size={16}
						color={folder.associationId ? theme.colors.onSurfaceVariant : theme.colors.primary}
					/>
					{/* minWidth: 0 — a flex column in RN-Web will not shrink below its content
					    without it, so a long folder name pushes the label off the row. */}
					<View style={{ flex: 1, minWidth: 0 }}>
						<Text style={{ fontSize: 14, fontWeight: '500' }} numberOfLines={1}>{folder.name}</Text>
						{/* Every claimant, named. A folder already serving another community is not
						    off-limits — two entities sharing one folder is exactly what a takeover
						    leaves behind — but pinning to it should be a decision made with the
						    current occupants in view rather than a surprise discovered later. */}
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }} numberOfLines={2}>
							{folder.bucket}
							{claimantLabel(folder)}
						</Text>
					</View>
				</View>
			</Pressable>
		);
	};

	return (
		<Modal visible={visible} onDismiss={onDismiss} dismissOnBackdrop>
			<View style={{ gap: 12, maxHeight: 560 }}>
				<Text style={{ fontSize: 18, fontWeight: '600' }}>Choose a Dropbox folder</Text>
				<Text style={{ fontSize: 13, color: theme.colors.onSurfaceVariant }}>
					{data?.unavailable === 'not-configured'
						? 'Dropbox is not connected, so there are no folders to choose from.'
						: `${folders.length} folders on the drive, ${unlinkedCount} not linked to any association. `
							+ 'Unlinked folders are listed first.'}
				</Text>

				<Searchbar
					value={query}
					onChangeText={setQuery}
					placeholder="Filter by folder or association name"
					testID="dropbox-folder-search"
				/>

				{isLoading && (
					<View style={{ paddingVertical: 24, alignItems: 'center', gap: 8 }}>
						<ActivityIndicator />
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
							Reading the drive…
						</Text>
					</View>
				)}

				{/* The error is SURFACED rather than rendered as an empty list. An empty picker
				    reads as "the drive has no folders", which would send someone looking for a
				    Dropbox problem that does not exist. */}
				{!!error && (
					<Text style={{ fontSize: 13, color: theme.colors.error }}>
						Could not read the folder list: {error instanceof Error ? error.message : 'unknown error'}
					</Text>
				)}

				{!isLoading && !error && (
					<ScrollView style={{ maxHeight: 380 }}>
						{filtered.length === 0
							? (
								<Text style={{ fontSize: 13, color: theme.colors.onSurfaceVariant, padding: 12 }}>
									No folder matches “{query}”.
								</Text>
							)
							: filtered.map(renderRow)}
					</ScrollView>
				)}

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
					<Button mode="outlined" onPress={onDismiss} testID="dropbox-folder-cancel">
						<Text>Cancel</Text>
					</Button>
				</View>
			</View>
		</Modal>
	);
};
